import { action, makeAutoObservable, observable } from "mobx";

export type SIRDetailsMode =
    | "left_only"
    | "intersection"
    | "right_only"
    | "shifted_from"
    | "shifted_to";

export type SIRSummaryCounts = {
    left_count: number;
    right_count: number;
    left_only_count: number;
    right_only_count: number;
    intersection_count: number;
    /** Boothwise only: voters in this booth in Before who are not in After at all. */
    totally_deleted_count?: number;
    /** Boothwise only: voters in this booth in Before who appear in another booth in After. */
    shifted_from_count?: number;
    /** Boothwise only: voters in this booth in After who were in another booth in Before. */
    shifted_to_count?: number;
};

export type SIRImpactBucket = Record<string, number>;

export type SIRSummary = {
    overall: SIRSummaryCounts;
    boothwise: Record<string, SIRSummaryCounts>;
    impact?: {
        deleted: SIRImpactBucket;
        added: SIRImpactBucket;
    };
};

export type SIRBoothSummaryRow = SIRSummaryCounts & { booth: string };

export type SIRDetailsResponse = {
    items: Record<string, unknown>[];
    total: number;
};

export class SIRStore {
    rootStore: any;

    @observable left: string = "";
    @observable right: string = "";
    @observable summary: SIRSummary | null = null;
    @observable detailsMode: SIRDetailsMode = "left_only";
    @observable details: SIRDetailsResponse = { items: [], total: 0 };
    @observable booths: string[] = [];
    @observable loading = false;
    @observable detailsLoading = false;
    @observable count = 100;
    @observable offset = 0;
    @observable error: string | null = null;
    @observable hasSearchedOnce = false;

    @observable lookupVoterId: string | null = null;
    @observable lookupResult: Record<string, unknown> | null = null;
    @observable lookupLoading = false;
    @observable exportDetailsLoading = false;

    constructor(rootStore: any) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setLeft = (name: string) => {
        this.left = name;
    };

    @action
    setRight = (name: string) => {
        this.right = name;
    };

    @action
    setBooths = (booths: string[]) => {
        this.booths = booths ?? [];
    };

    @action
    setDetailsMode = (mode: SIRDetailsMode) => {
        this.detailsMode = mode;
        this.offset = 0;
    };

    @action
    setSummary = (data: SIRSummary | null) => {
        this.summary = data;
        this.error = null;
    };

    @action
    setDetails = (data: SIRDetailsResponse) => {
        this.details = data ?? { items: [], total: 0 };
    };

    @action
    setLoading = (value: boolean) => {
        this.loading = value;
    };

    @action
    setDetailsLoading = (value: boolean) => {
        this.detailsLoading = value;
    };

    @action
    setError = (message: string | null) => {
        this.error = message;
    };

    @action
    setHasSearchedOnce = () => {
        this.hasSearchedOnce = true;
    };

    @action
    setPagination = (count: number, offset: number) => {
        this.count = count;
        this.offset = offset;
    };

    @action
    reset = () => {
        this.summary = null;
        this.details = { items: [], total: 0 };
        this.loading = false;
        this.detailsLoading = false;
        this.error = null;
        this.hasSearchedOnce = false;
        this.offset = 0;
    };

    @action
    setLookupVoterId = (id: string | null) => {
        this.lookupVoterId = id;
    };

    @action
    setLookupResult = (result: Record<string, unknown> | null) => {
        this.lookupResult = result;
    };

    @action
    setLookupLoading = (value: boolean) => {
        this.lookupLoading = value;
    };

    @action
    setExportDetailsLoading = (value: boolean) => {
        this.exportDetailsLoading = value;
    };

    @action
    clearLookup = () => {
        this.lookupVoterId = null;
        this.lookupResult = null;
        this.lookupLoading = false;
    };

    fetchVoterLookup = async (
        projectid: string | number | undefined,
        voterId: string
    ): Promise<void> => {
        if (!projectid || !voterId?.trim()) return;
        this.setLookupVoterId(voterId.trim());
        this.setLookupResult(null);
        this.setLookupLoading(true);
        try {
            const body = {
                projectid: typeof projectid === "string" ? parseInt(projectid, 10) : projectid,
                voter_id: voterId.trim(),
            };
            const res = await this.rootStore.api.post("qfast/sir/voter-lookup", body);
            this.setLookupResult((res.data ?? null) as Record<string, unknown> | null);
        } catch (err: any) {
            this.setLookupResult(null);
            if (!this.error) this.setError(getApiErrorMessage(err, "Voter lookup failed"));
        } finally {
            this.setLookupLoading(false);
        }
    };

    fetchSummary = async (projectid: string | undefined): Promise<void> => {
        if (!projectid || !this.left || !this.right) return;
        this.setLoading(true);
        this.setError(null);
        try {
            const body = {
                projectid: parseInt(projectid, 10),
                left: this.left,
                right: this.right,
                booths: this.booths.length > 0 ? this.booths : undefined,
            };
            const res = await this.rootStore.api.post("qfast/sir/summary", body);
            const data = res.data as SIRSummary;
            this.setSummary(data);
            this.setHasSearchedOnce();
            this.offset = 0;
        } catch (err: any) {
            this.setError(getApiErrorMessage(err, "Summary failed"));
        } finally {
            this.setLoading(false);
        }
    };

    fetchDetails = async (projectid: string | undefined): Promise<void> => {
        if (!projectid || !this.left || !this.right) return;
        this.setDetailsLoading(true);
        try {
            const body = {
                projectid: parseInt(projectid, 10),
                left: this.left,
                right: this.right,
                mode: this.detailsMode,
                count: this.count,
                offset: this.offset,
                booths: this.booths.length > 0 ? this.booths : undefined,
            };
            const res = await this.rootStore.api.post("qfast/sir/details", body);
            const data = res.data as SIRDetailsResponse;
            this.setDetails(data ?? { items: [], total: 0 });
        } catch (err: any) {
            this.setDetails({ items: [], total: 0 });
            if (!this.error) this.setError(getApiErrorMessage(err, "Details failed"));
        } finally {
            this.setDetailsLoading(false);
        }
    };

    fetchDetailsExport = async (projectid: string | undefined): Promise<void> => {
        if (!projectid || !this.left || !this.right) return;
        if (this.exportDetailsLoading) return;
        this.setExportDetailsLoading(true);
        try {
            const body = {
                projectid: parseInt(projectid, 10),
                left: this.left,
                right: this.right,
                mode: this.detailsMode,
                booths: this.booths.length > 0 ? this.booths : undefined,
            };
            const res = await this.rootStore.api.post("qfast/sir/export", body, {
                responseType: "blob",
            });
            if (res.status && res.status >= 400) {
                const msg = await blobToErrorMessage(res.data as Blob);
                this.setError(msg);
                return;
            }
            const blob = res.data as Blob;
            const filename =
                parseFilenameFromContentDisposition(
                    res.headers?.["content-disposition"] ?? res.headers?.["Content-Disposition"]
                ) ||
                `sir_details_${this.detailsMode}_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.csv`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            this.setError(getApiErrorMessage(err, "Export failed"));
        } finally {
            this.setExportDetailsLoading(false);
        }
    };
}

const MAX_ERROR_LENGTH = 280;

function truncateMessage(s: string): string {
    if (s.length <= MAX_ERROR_LENGTH) return s;
    return s.slice(0, MAX_ERROR_LENGTH).trim() + "…";
}

function getApiErrorMessage(err: any, fallback: string): string {
    if (!err) return fallback;
    const data = err.response?.data;
    if (data != null && typeof data === "object") {
        const d = data.detail;
        if (typeof d === "string") return truncateMessage(d);
        if (Array.isArray(d) && d.length > 0) {
            const first = d[0];
            if (first?.msg != null) return truncateMessage(String(first.msg));
            if (typeof first === "string") return truncateMessage(first);
        }
        if (typeof data.message === "string") return truncateMessage(data.message);
    }
    if (typeof data === "string" && data.length > 0) return truncateMessage(data);
    const msg = err?.message;
    if (typeof msg === "string" && msg.length > 0) {
        if (msg.includes("status code")) return `${fallback}. Please try again.`;
        return msg.length < 300 ? msg : fallback;
    }
    return fallback;
}

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
    if (!header) return null;
    const m =
        header.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i) ||
        header.match(/filename=["']?([^"';\n]+)["']?/i);
    return m ? m[1].trim() : null;
}

async function blobToErrorMessage(blob: Blob): Promise<string> {
    try {
        const text = await blob.text();
        const j = JSON.parse(text);
        return (j.detail ?? j.message ?? text) || "Export failed";
    } catch {
        return "Export failed";
    }
}
